import { FormEvent, useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import PageHero from '../../components/PageHero';
import { FieldLabel, TextInput, TextTextarea, FormButton, HoneypotInput } from '../../components/forms/FormFields';
import RecaptchaField from '../../components/RecaptchaField';
import { notify } from '../../components/notify/notify';
import { useSilentGuard } from '../../hooks/useSilentGuard';
import { useCaptcha } from '../../hooks/useCaptcha';
import { useAppSelector } from '../../hooks/redux';
import { submitContact } from '../../services/leads';
import { clientError, ServerErrorCode, toAppError } from '../../lib/errorCodes';
import { EMAIL_RE, PHONE_RE } from '../../lib/validation';

const EMPTY_FORM = { name: '', email: '', phone: '', message: '' };

export default function ContactPage() {
  const content = useAppSelector((s) => s.geo.content);
  const { state, setHoneypot, validate, reset } = useSilentGuard();
  const captcha = useCaptcha();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const phone = form.phone.trim();
    const message = form.message.trim();

    if (!name || !email || !message) {
      notify.appError(clientError('REQUIRED_FIELDS', 'Please enter your name, email, and message.'));
      return;
    }
    if (!EMAIL_RE.test(email)) {
      notify.appError(clientError('INVALID_EMAIL'));
      return;
    }
    if (phone && !PHONE_RE.test(phone)) {
      notify.appError(clientError('INVALID_PHONE'));
      return;
    }
    if (!captcha.verified) {
      notify.appError(clientError('CAPTCHA_REQUIRED'));
      return;
    }

    const gate = await validate();
    if (!gate.ok) {
      notify.appError(gate.error);
      return;
    }

    setLoading(true);
    try {
      await submitContact({
        name,
        email,
        phone: phone || undefined,
        message,
        country: content.countryCode,
        captchaId: captcha.verified.captchaId,
        captchaAnswer: captcha.verified.captchaAnswer,
        silent: gate.payload,
      });
      notify.success({ title: 'Message sent', message: 'Thanks for reaching out — the Micus team will reply soon.' });
      setForm(EMPTY_FORM);
      reset();
      await captcha.reload();
    } catch (err) {
      const info = toAppError(err);
      notify.appError(info);
      if (info.code === ServerErrorCode.CAPTCHA_FAILED) await captcha.reload();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero badge="Contact" title="We're here" titleAccent="to help" subtitle="Questions about rating, fundraising, or your account — reach the Micus team." />
      <section style={{ padding: '64px 0', background: '#FFFBF8' }}>
        <div className="container-wide" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32 }}>
          <div style={{ background: 'white', borderRadius: 20, border: '1px solid #F0C8D8', padding: 28 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Contact details</h2>
            {[
              { icon: Mail, label: 'Email', value: content.email, href: `mailto:${content.email}` },
              { icon: Phone, label: 'Phone', value: content.phone, href: content.phoneHref },
              { icon: MapPin, label: 'Office', value: content.address, href: null },
            ].map(({ icon: Icon, label, value, href }) => (
              <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#C2185B,#E91E63)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} color="#fff" />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 12, color: '#9B6E7E', fontWeight: 700 }}>{label}</p>
                  {href ? <a href={href} style={{ fontSize: 14, fontWeight: 600, color: '#1A0A10' }}>{value}</a> : <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{value}</p>}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={onSubmit} style={{ background: 'white', borderRadius: 20, border: '1px solid #F0C8D8', padding: 28 }}>
            <HoneypotInput value={state.honeypot} onChange={setHoneypot} />
            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <FieldLabel htmlFor="c-name">Name</FieldLabel>
                <TextInput id="c-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </div>
              <div>
                <FieldLabel htmlFor="c-email">Email</FieldLabel>
                <TextInput id="c-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
              </div>
              <div>
                <FieldLabel htmlFor="c-phone">Phone (optional)</FieldLabel>
                <TextInput id="c-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </div>
              <div>
                <FieldLabel htmlFor="c-msg">Message</FieldLabel>
                <TextTextarea id="c-msg" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} required />
              </div>
              <RecaptchaField
                challenge={captcha.challenge}
                loading={captcha.loading}
                onRefresh={() => void captcha.reload()}
                onVerified={captcha.setVerified}
              />
              <FormButton type="submit" loading={loading}>Send message</FormButton>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
